import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchLeads, type Lead, type PagedResponse } from '@/lib/supportAdminApi'
import { fmtDate } from './AdminSuporte'

export default function AdminLeads() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  const [data, setData] = useState<PagedResponse<Lead> | null>(null)
  const [dataLoading, setDataLoading] = useState(true)
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (loading) return
    if (!user) navigate('/auth', { replace: true })
  }, [user, loading, navigate])

  const load = useCallback(async () => {
    setDataLoading(true)
    try {
      const res = await fetchLeads({ page })
      setData(res)
    } catch { /* ignore */ }
    finally { setDataLoading(false) }
  }, [page])

  useEffect(() => { load() }, [load])

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )

  if (!user) return null

  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Leads</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Usuários que informaram e-mail no chat mas ainda não possuem conta.
        </p>
      </div>

      <Card className="p-6">
        <div className="flex justify-end mb-4">
          <Button size="sm" variant="outline" onClick={load}>Atualizar</Button>
        </div>

        {dataLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Capturado em</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      Nenhum lead capturado ainda.
                    </TableCell>
                  </TableRow>
                )}
                {data?.items.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="text-sm font-medium">{lead.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{lead.source}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      {lead.converted_at ? (
                        <span className="text-xs text-emerald-600">
                          Convertido em {fmtDate(lead.converted_at)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Não convertido</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground mt-4">
                <span>{data?.total} leads</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="icon" variant="outline" className="h-7 w-7"
                    disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span>Página {page} de {totalPages}</span>
                  <Button
                    size="icon" variant="outline" className="h-7 w-7"
                    disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
